import { Data, Utils } from 'common';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import moment from 'moment';
import { ActiveMenuItemId } from '../shared/ActiveMenuItemId.enum';
import { DateRange } from '../shared/DateRangeOptions';
import DateRangeType from '../shared/DateRangeType.enum';
import { EligibleFor } from '../shared/EligibleForOptions';
import { PrintStatus } from '../shared/StatusOptions';
import { TrackingNumberType } from '../shared/TrackingNumberType.enum';

const { Session, Util: CommonUtil } = Utils;
const { Carrier } = Data;

class Util {
	static buildQueryString = () => ({});

	/**
	 * Convert GMT date string in ISO 8601 format to date object in local time.
	 *
	 * Note:
	 * The Offset is based on Session settings provided, we then calculate the DST Offset
	 * based on the time DST starts/ends and after that we apply the final offset to the time.
	 *
	 * @param {string} gmtTime ISO 8601 format date string, e.g. 2021-04-09T22:05:29.128Z.
	 * @returns {Date|*} Return `null` if no gmtTime is provided.
	 */
	static convertGmtTimeToLocalTime = gmtTime => {
		if (!isString(gmtTime) || gmtTime === '0001-01-01T00:00:00') {
			return null;
		}

		const { GMTOffsetMinutes = -480, DaylightSavingsTime = true } =
			Session.getSettings();
		const utcMoment = moment.utc(gmtTime, moment.ISO_8601);

		const shouldApplyDaylightSavingOffset =
			DaylightSavingsTime === true && this.isDST(utcMoment);

		// calculate the GMT Offset in minutes based on the daylight saving factor
		utcMoment.utcOffset(
			GMTOffsetMinutes + (shouldApplyDaylightSavingOffset ? 60 : 0)
		);

		return utcMoment.isValid()
			? new Date(utcMoment.format('YYYY-MM-DD[T]HH:mm:ss'))
			: null;
	};

	/**
	 * Calculate the date at meter's location from the local date, and
	 * return the date string in ISO 8601 format.
	 *
	 * @param {Date|moment} localDate A Date or moment object.
	 * @returns {string|*} Return `null` if date is not provided.
	 */
	static convertLocalDateToGmtString = localDate => {
		if (!moment.isDate(localDate) && !moment.isMoment(localDate)) {
			return null;
		}

		// create a moment object
		const localMoment = moment.isMoment(localDate)
			? localDate
			: moment(localDate);

		// calculate the delta between the utcOffset at user's current location and the utcOffset at meter's location
		const utcOffsetDelta = this.getUtcOffsetDelta();

		/**
		 * If delta is positive, the time at the current location is ahead of meter's location;
		 * if delta is negative, the time at the current location is behind meter's location.
		 *
		 * Example:
		 * LA: 2021-08-27 (-420)=> 2021-07-27T07:00:00.000Z
		 * NY: 2021-08-27 (-240)=> 2021-07-27T04:00:00.000Z
		 */
		return utcOffsetDelta !== 0
			? localMoment.add(utcOffsetDelta, 'm').toISOString()
			: localMoment.toISOString();
	};

	/**
	 * Return `true` if the GMT moment falls between the 2nd Sunday of March and 1st Sunday of November,
	 * i.e. daylight saving time (DST) is in effect.
	 *
	 * @param {moment} gmtTime A moment object.
	 * @returns {boolean}
	 */
	static isDST = gmtTime => {
		if (!moment.isMoment(gmtTime) || !gmtTime.isValid()) {
			return false;
		}

		const { GMTOffsetMinutes = -480 } = Session.getSettings();
		const year = gmtTime.year();
		const start = moment.utc(`${year}-03-01 02:00:00`);
		const end = moment.utc(`${year}-11-01 02:00:00`);

		const daysFromSecondSunday = start.day() === 0 ? 7 : 14 - start.day();
		const secondSundayOfMarchInGMT = start
			.add(daysFromSecondSunday, 'days')
			.add(-1 * GMTOffsetMinutes, 'minutes');

		const firstSundayOfNovemberInGMT = end
			.add((7 - end.day()) % 7, 'days')
			.add(-1 * GMTOffsetMinutes, 'minutes');

		return gmtTime.isBetween(
			secondSundayOfMarchInGMT,
			firstSundayOfNovemberInGMT
		);
	};

	/**
	 * Calculate the delta between the utc-offset at user's current location and
	 * the utc-offset at meter's location.
	 * @returns {number}
	 */
	static getUtcOffsetDelta = () => {
		// get the GMT offset and daylight savings flag of meter's account
		const { GMTOffsetMinutes = -480, DaylightSavingsTime = true } =
			Session.getSettings();

		// create a current moment object
		const localMoment = moment();

		// check whether daylight saving should be applied
		const shouldApplyDaylightSavingOffset =
			DaylightSavingsTime === true && this.isDST(localMoment);

		return (
			localMoment.utcOffset() -
			(GMTOffsetMinutes + (shouldApplyDaylightSavingOffset ? 60 : 0))
		);
	};

	/**
	 * Calculate the start of date range at meter's location and return a moment object.
	 * @param {date} localDate A local date object
	 * @returns {moment}
	 */
	static calculateStartDate = localDate => {
		if (!moment.isDate(localDate)) {
			return null;
		}

		const momentAtMeterLocation = moment(localDate);
		const fullYear = momentAtMeterLocation.get('year');
		const month = momentAtMeterLocation.get('month') + 1;
		const date = momentAtMeterLocation.get('date');
		const start = moment(`${fullYear}-${month}-${date}`, 'YYYY-MM-DD');

		return start;
	};

	/**
	 * Calculate the end of date range at meter's location and return a moment object.
	 * @param {date} localDate A local date object
	 * @returns {moment}
	 */
	static calculateEndDate = localDate => {
		const end = this.calculateStartDate(localDate);

		return moment.isMoment(end) ? end.add(1, 'day') : null;
	};

	/**
	 * Return a date range object containing keys Prints API recognizes.
	 * @param {DateRangeType} dateRangeType
	 * @param {date} dateRangeStart The start date.
	 * @param {date} dateRangeEnd The end date.
	 * @returns {object}
	 */
	static buildDateRange = (dateRangeType, dateRangeStart, dateRangeEnd) => {
		// date-range start
		const startDateAtMeterLocation = this.calculateStartDate(dateRangeStart);
		const dateRangeStartInISO8601 = this.convertLocalDateToGmtString(
			startDateAtMeterLocation
		);

		// date-range end
		const endDateAtMeterLocation = this.calculateEndDate(dateRangeEnd);
		const dateRangeEndInISO8601 = this.convertLocalDateToGmtString(
			endDateAtMeterLocation
		);

		// date-range transaction_date_start for DeliveredDate and ShipDate
		const startTranDateAtMeterLocation = this.calculateStartDate(
			dateRangeStart
		).subtract(1, 'year');
		const dateRangeTranStartInISO8601 = this.convertLocalDateToGmtString(
			startTranDateAtMeterLocation
		);

		if (dateRangeType === DateRangeType.DeliveredDate) {
			// delivery date
			return {
				delivered_date_start: dateRangeStartInISO8601,
				delivered_date_end: dateRangeEndInISO8601,
				transaction_date_start: dateRangeTranStartInISO8601,
				transaction_date_end: dateRangeEndInISO8601,
			};
		} else if (dateRangeType === DateRangeType.ShipDate) {
			// ship date
			return {
				ship_date_start: dateRangeStartInISO8601,
				ship_date_end: dateRangeEndInISO8601,
				transaction_date_start: dateRangeTranStartInISO8601,
				transaction_date_end: dateRangeEndInISO8601,
			};
		}

		// default to transaction date
		return {
			transaction_date_start: dateRangeStartInISO8601,
			transaction_date_end: dateRangeEndInISO8601,
		};
	};

	/**
	 * Return a object contains eligible for keys Prints API recognizes.
	 * @param {EligibleFor} eligibleFor
	 * @returns {object}
	 */
	static buildEligibleFor = eligibleFor => ({
		eligible_for_refund_assist:
			eligibleFor === EligibleFor.RefundAssist ? true : null,
		eligible_for_container_label:
			eligibleFor === EligibleFor.Containerlabel ? true : null,
		eligible_for_refund: eligibleFor === EligibleFor.Refund ? true : null,
		eligible_for_manifest: eligibleFor === EligibleFor.Manifest ? true : null,
		eligible_for_insurance_claim:
			eligibleFor === EligibleFor.InsuranceClaim ? true : null,

		// TODO: get clarification from Products how to use these flags
		// eligible_for_carrier_pickup: null,
		// eligible_for_return_label: null,
	});

	/**
	 * Return the parameter object for `buildGetPrintsPayload` method based on the clicked
	 * left filter menu item.
	 * @returns {object}
	 */
	static buildSearchFilterByActiveMenuItem = (activeMenuItem = {}) => {
		const { activeMenuItemId, activeMenuItemValue, term } = activeMenuItem;

		// search filter
		const search =
			activeMenuItemId === ActiveMenuItemId.Search
				? {
						keyword_search: term,
					}
				: {};

		// date range filter
		const { dateRangeStart, dateRangeEnd } =
			activeMenuItemId === ActiveMenuItemId.DatePrinted
				? CommonUtil.calculateDateRange(activeMenuItemValue)
				: CommonUtil.calculateDateRange(DateRange.All);

		const dateRange = this.buildDateRange(
			DateRangeType.TransactionDate,
			dateRangeStart,
			dateRangeEnd
		);

		// eligible-for filter
		const eligibleFor =
			activeMenuItemId === ActiveMenuItemId.EligibleFor
				? this.buildEligibleFor(activeMenuItemValue)
				: {};

		// status filter
		const status =
			activeMenuItemId === ActiveMenuItemId.Status
				? {
						status: [activeMenuItemValue],
					}
				: {};

		// user filter
		const user =
			activeMenuItemId === ActiveMenuItemId.User
				? {
						user_id: activeMenuItemValue,
					}
				: {};

		return {
			...search,
			...dateRange,
			...eligibleFor,
			...status,
			...user,
		};
	};

	/**
	 * Return the parameter object for `buildGetPrintsPayload` method based on the inputs in
	 * "Advanced Search" modal.
	 * @returns {object}
	 */
	static buildSearchFilterByAdvancedSearchFilter = advancedSearchFilter => {
		const { dateRangeType, dateRangeStart, dateRangeEnd } =
			advancedSearchFilter || {};
		const dateRange = this.buildDateRange(
			dateRangeType,
			dateRangeStart,
			dateRangeEnd
		);
		const payload = {
			...dateRange,
			status:
				isString(advancedSearchFilter.status) &&
				advancedSearchFilter.status !== ''
					? [advancedSearchFilter.status]
					: null,
			memo:
				isString(advancedSearchFilter.printMessage) &&
				advancedSearchFilter.printMessage !== ''
					? [advancedSearchFilter.printMessage]
					: null,
			tracking_numbers:
				isString(advancedSearchFilter.trackingNumbers) &&
				advancedSearchFilter.trackingNumbers !== ''
					? [advancedSearchFilter.trackingNumbers]
					: null,
			carriers:
				isString(advancedSearchFilter.carriers) &&
				advancedSearchFilter.carriers !== ''
					? [advancedSearchFilter.carriers]
					: null,
			service_types:
				isString(advancedSearchFilter.serviceTypes) &&
				advancedSearchFilter.serviceTypes !== ''
					? [advancedSearchFilter.serviceTypes]
					: null,
			cost_codes: isNumber(advancedSearchFilter.costCodes)
				? [`${advancedSearchFilter.costCodes}`]
				: null,
			refund_type:
				isString(advancedSearchFilter.refundType) &&
				advancedSearchFilter.refundType !== ''
					? advancedSearchFilter.refundType
					: null,
			to_name:
				isString(advancedSearchFilter.toName) &&
				advancedSearchFilter.toName !== ''
					? advancedSearchFilter.toName
					: null,
		};

		// remove user_id from non-user filter calls
		if (isNumber(advancedSearchFilter.user)) {
			payload.user_id = advancedSearchFilter.user;
		}

		return payload;
	};

	/**
	 * Build the payload object to Prints API.
	 * @param {object} searchFilter The search criteria.
	 * @param {string} sort Sort by column.
	 * @returns {object}
	 */
	static buildGetPrintsPayload = (
		searchFilter = {},
		sort,
		activeMenuItemId
	) => {
		const { userId = '' } = Session.getAccountInfo();
		const payload = {
			...searchFilter,
			sort,
		};

		// remove user_id from non-user filter calls
		if (activeMenuItemId === ActiveMenuItemId.User) {
			payload.user_id = isNumber(searchFilter.user_id)
				? searchFilter.user_id
				: userId; // this might not be needed in the future
		}

		return payload;
	};

	/**
	 * Return the sort enum value Server recognizes.
	 * @param {array} sortModel The object ag-grid returns after user clicks on the column header.
	 * (https://www.ag-grid.com/archive/22.1.1/javascript-grid-sorting/#sorting-api)
	 * @returns {null|string}
	 */
	static buildSortBy = sortModel => {
		const { colId, sort: direction } =
			isArray(sortModel) && isObject(sortModel[0]) ? sortModel[0] : {};
		// Columns set to false are capable of being sorted through the endpoint but are
		// not currently used in the grid. They are in here for future reference.
		const sortableColumns = {
			advanced_options_names: false, // Extra Services
			carrier: true, // Carrier
			cost_code: true, // Cost Code
			created_date: true, // Date Printed
			cust_id: false,
			delivered_date: true, // Date Delivered
			eligible_for_carrier_pickup: false,
			eligible_for_container_label: false,
			eligible_for_insurance_claim: false,
			eligible_for_manifest: false,
			eligible_for_refund: false,
			eligible_for_return_label: false,
			from_address_postal_code: true, // Origin Zip
			insurance_type_name: true, // Insurance Provider
			insured_value: true, // Insured For
			label_id: false,
			memo: true, // Printed Message
			packaging_type: false,
			payment_type: true, // Payment Type
			recipient_name: true, // Name
			reference_field_1: true, // Reference1
			refund_type: true, // Refund Type
			service_type: true, // Service
			ship_date: true, // Ship Date
			status: true, // Shipment Status
			total_adjusted_amount: true, // Adjusted Amount
			total_amount: true, // Amount Paid
			total_estimated_amount: true, // Quoted Amount
			tracking_number: true, // Tracking Number
			transaction_date: false,
			universal_id: false,
			username: true, // User
			user_id: false,
			weight_pounds: true, // Weight
		};

		return sortableColumns[colId] === true
			? `${colId}_${isString(direction) ? direction : 'asc'}`
			: null;
	};

	/**
	 * Return an address object where keys are in snake_case.
	 * @param {object} address Address object where keys are in camelCase.
	 * @returns {object}
	 */
	static buildAddressForSERA = address => ({
		company_name: address.companyName,
		name: address.name,
		address_line1: address.addressLine1,
		address_line2: address.addressLine2,
		address_line3: address.addressLine3,
		city: address.city,
		state_province: address.stateProvince,
		postal_code: address.postalCode,
		country_code: address.countryCode,
		phone: address.phone,
		email: address.email,
	});

	static showCheckClaimStatus = (insuredValue, insuranceType) =>
		isNumber(insuredValue) &&
		isString(insuranceType) &&
		insuredValue > 0 &&
		insuranceType === 'parcelguard';

	static showTracking = (trackingNumber, trackingNumberType) =>
		isString(trackingNumber) && trackingNumberType !== TrackingNumberType.Imb;

	static formattedServiceTypeName = (
		isReturnLabel,
		carrier,
		serviceTypeName
	) =>
		isReturnLabel && carrier === Carrier.UPS
			? `${serviceTypeName} Returns`
			: serviceTypeName;

	static getEligibilityForReprint = (
		printStatus,
		carrier,
		transactionDate,
		printTypeName
	) => {
		let isEligibleByDate = false;
		const hourseSinceTransaction = moment().diff(transactionDate, 'hours');
		const daysSinceTransaction = moment().diff(transactionDate, 'days');
		let timeFrame = '';
		let ineligibilityReason = null;
		let isEligible = false;

		// Disable reprint for "Mailing Label" or "MailingLabel" printTypeName
		// Normalize by removing spaces and converting to lowercase for robust comparison
		if (printTypeName?.replace(/\s+/g, '').toLowerCase() === 'mailinglabel') {
			return {
				isEligible: false,
				ineligibilityReason: 'This item is not eligible for reprinting.',
			};
		}

		if (!printStatus) {
			return {
				isEligible,
				ineligibilityReason,
			};
		}

		if (carrier === Carrier.USPS || carrier === Carrier.GlobalPost) {
			isEligibleByDate = daysSinceTransaction === 0;
			timeFrame = '24-hour';
		} else if (carrier === Carrier.FedEx) {
			isEligibleByDate = hourseSinceTransaction <= 12;
			timeFrame = '12-hour';
		} else if (carrier === Carrier.UPS || carrier === Carrier.DHLExpress) {
			isEligibleByDate = daysSinceTransaction <= 90;
			timeFrame = '90-day';
		}

		isEligible =
			isEligibleByDate &&
			printStatus !== PrintStatus.RefundPending &&
			printStatus !== PrintStatus.Pending &&
			printStatus !== PrintStatus.Refunded;

		ineligibilityReason = !isEligible
			? isEligibleByDate
				? 'This item is not eligible for reprinting.'
				: `${timeFrame} reprint window expired.`
			: ineligibilityReason;

		return {
			isEligible,
			ineligibilityReason,
		};
	};
}
export default Util;
